import React from 'react';
import { getIcon } from '~/design-system/icons/icon-registry';
import { cn } from "~/lib/utils";

export interface DeckIconProps extends React.HTMLAttributes<HTMLElement> {
  name: string;
  size?: 'button-lg' | 'button-default' | 'button-xs' | 'button-2xs' | number;
  className?: string;
  preserveColors?: boolean;
}

const sizeMap = {
  'button-lg': 20,
  'button-default': 16,
  'button-xs': 18,
  'button-2xs': 16,
};

/**
 * Icon markup is derived by running four regexes over the registry's SVG string, and the result depends on
 * nothing but (name, size, preserveColors) — so it is computed once and reused. This matters in bulk: the
 * agent rail alone mounts ~137 icons per open, and the identical string also lets React skip re-writing
 * innerHTML on re-render, since it diffs `__html` by value.
 */
const svgCache = new Map<string, string>();

const buildSvg = (name: string, iconSize: number, preserveColors: boolean): string | null => {
  const key = `${name}|${iconSize}|${preserveColors ? 1 : 0}`;
  const cached = svgCache.get(key);
  if (cached !== undefined) return cached;

  const raw = getIcon(name);
  if (!raw) return null;

  let svgContent = raw;
  // Replace hardcoded fills with currentColor for proper color inheritance (unless preserveColors is true)
  if (!preserveColors) {
    svgContent = svgContent
      .replace(/fill="#[0-9A-Fa-f]{6}"/g, 'fill="currentColor"')
      .replace(/fill="#[0-9A-Fa-f]{3}"/g, 'fill="currentColor"');
  }

  svgContent = svgContent
    .replace(/width="\d+"/, `width="${iconSize}"`)
    .replace(/height="\d+"/, `height="${iconSize}"`);

  svgCache.set(key, svgContent);
  return svgContent;
};

const DeckIconImpl = React.forwardRef<HTMLElement, DeckIconProps>(
  ({ name, size = 'button-default', className, preserveColors = false, ...props }, ref) => {
    const iconSize = typeof size === 'number' ? size : sizeMap[size];
    const svgContent = buildSvg(name, iconSize, preserveColors);

    if (!svgContent) {
      console.warn(`DeckIcon: Icon "${name}" not found in registry`);
      return null;
    }

    return (
      <i
        ref={ref as React.Ref<HTMLElement>}
        className={cn('deck-icon', className)}
        style={{ 
          fontSize: iconSize, 
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
        {...props}
      />
    );
  }
);

DeckIconImpl.displayName = "DeckIcon";

/** Icons are pure in their props, and they mount in bulk (whole chat threads, toolbars, every slide's
 *  chrome), so skipping their re-render on unrelated parent updates is worth the comparison. */
export const DeckIcon = React.memo(DeckIconImpl);

